import React from 'react';
import PropTypes from 'prop-types';
import { AgencyPage } from '../../components/Views/AgencyPage';
import Layout from '../../components/Layout/Layout';
import initApollo from '../../utils/init-Apollo';
import { getStaticData } from '../../utils/helpers/getStaticData';
import { getAgencySeoData } from '../../utils/helpers/seo';
import { getSelectedAgencyQuery } from '../../api/queries';
import { normalizePath } from '../../utils/helpers';
import { withBugsnagGetStaticProps } from '../../utils/withBugsnag';
import { AGENCY_REVALIDATE_SECONDS } from '../../utils/constants/agencyPage';
import { loadAgencyMockData } from '../../utils/helpers/loadAgencyMockData';
import { extractApolloState } from '../../utils/helpers/extractApolloState';

const Agency = ({ agencySlug, hasError }) => (
  <AgencyPage agencySlug={agencySlug} hasError={hasError} />
);

export const getStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking'
});

const isE2E = process.env.NEXT_PUBLIC_E2E === '1';

export const getStaticProps = withBugsnagGetStaticProps(
  async ({ params, locale }) => {
    const slug = params.agency;
    const asPath = `/agentlikler/${slug}`;
    const newAsPath = normalizePath(asPath);

    const apolloClient = initApollo({}, { lang: locale });

    if (isE2E && process.env.NODE_ENV !== 'production') {
      try {
        await loadAgencyMockData(apolloClient);

        return extractApolloState(apolloClient, {
          props: {
            seoData: null,
            agencySlug: 'realemlaknizami',
            currentLang: locale
          },
          revalidate: AGENCY_REVALIDATE_SECONDS
        });
      } catch (error) {
        console.error('Error loading mock data:', error);
      }
    }

    const [agencyRes] = await Promise.all([
      apolloClient.query({
        query: getSelectedAgencyQuery,
        variables: { slug },
        errorPolicy: 'all'
      }),
      getStaticData(apolloClient)
    ]);

    const agency = agencyRes?.data?.agency ?? null;
    const hasGraphQLErrors = Boolean(agencyRes?.errors?.length);

    if (!agency && hasGraphQLErrors) {
      return {
        props: {
          seoData: null,
          agencySlug: slug,
          hasError: true,
          currentLang: locale
        },
        revalidate: AGENCY_REVALIDATE_SECONDS
      };
    }

    if (!agency && !hasGraphQLErrors) {
      return {
        notFound: true,
        revalidate: AGENCY_REVALIDATE_SECONDS
      };
    }

    return extractApolloState(apolloClient, {
      props: {
        seoData: getAgencySeoData(agency?.name, locale, newAsPath),
        agencySlug: slug,
        hasError: false,
        currentLang: locale
      },
      revalidate: AGENCY_REVALIDATE_SECONDS
    });
  },
  'agencies/[agency]'
);

Agency.getLayout = (page, pageProps) => (
  <Layout
    isBottomNavigation={false}
    isBanner={true}
    isShowBackButton
    isShowCloseButton={false}
    pageProps={pageProps}
    hideMobileHeader
  >
    {page}
  </Layout>
);

Agency.propTypes = {
  agencySlug: PropTypes.string,
  hasError: PropTypes.bool
};

export default Agency;
